import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Flex, FormControl, FormLabel, HStack, IconButton, Image, Input,
  Menu, MenuButton, MenuDivider, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Portal,
  Slider, SliderFilledTrack, SliderThumb, SliderTrack, Spinner, Stack, Text,
  useDisclosure, useToast,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { ID3Writer } from 'browser-id3-writer';
import { RootState } from '../redux/store';
import {
  collapse, cycleRepeat, next, pause, play, prev, seek, toggleShuffle,
} from '../redux/features/playerSlice';
import {
  useDeleteJobMutation, useGenerateCoverMutation, useRenameJobMutation, useUploadCoverMutation,
} from '../redux/services/mixgenie';
import { mixgeniePublicServiceURL } from '../redux/services/defines';
import { downloadBlobFromUrl } from '../download/downloadBlob';
import { resizeImageForUpload } from '../download/resizeImage';
import { VideoStudioModal } from '../video/VideoStudioModal';
import { PictoolsMark } from '../components/PictoolsMark';
import { RhythmBlobs } from './RhythmBlobs';

/**
 * Full-screen "now playing" view — SoundCloud / Spotify / stock Android
 * music-player conventions: giant cover art, big transport row with
 * repeat + shuffle flanking prev/play/next, seek bar with tabular-num
 * times, an actions row (favourite, share, download) at the bottom, and
 * a swipe-down / X to collapse back to the sticky bar.
 *
 * The component is a Chakra Modal (full-viewport, no card chrome) mounted
 * globally alongside the sticky bar. Redux state (`player.expanded`) is
 * the single source of truth for open/closed — tapping the sticky bar
 * toggles it.
 */
export function FullPlayer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const renameModal = useDisclosure();
  const videoModal = useDisclosure();
  const [renameDraft, setRenameDraft] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameJob] = useRenameJobMutation();
  const [deleteJob] = useDeleteJobMutation();
  const { queue, index, isPlaying, progress, duration, expanded, repeatMode, shuffle } =
    useSelector((s: RootState) => s.player);
  const track = index >= 0 ? queue[index] : null;

  // Cross-sell for pictools.ai — shown in the cover slot when a song has no
  // artwork yet. Same 1-credit mutation as the row menu, kept independent
  // so the two surfaces can evolve separately.
  const [generateCoverMut, { isLoading: coverBusy }] = useGenerateCoverMutation();
  const generateCoverNow = async () => {
    if (!track?.id) return;
    try {
      await generateCoverMut({ jobId: track.id }).unwrap();
      toast({ title: t('player.coverDone', 'Cover generated — updating…'), status: 'success', duration: 2000 });
      // The cover URL will land on the queue track via the poll refresh +
      // updateTrack mirror on HistoryPage / GeneratorPage. Nothing to do here.
    } catch (e: any) {
      toast({ title: e?.data?.message || t('player.coverFail', 'Cover generation failed — try again'), status: 'error' });
    }
  };

  // Custom cover from device (gallery / camera). The <input capture> attribute
  // asks Android/iOS to show camera as a source alongside gallery; without it
  // the OS opens the gallery-only picker on some devices. Free — user's own asset.
  const [uploadCoverMut, { isLoading: coverUploadBusy }] = useUploadCoverMutation();
  const coverFileInput = useRef<HTMLInputElement | null>(null);
  const pickCoverFile = () => coverFileInput.current?.click();
  const onCoverFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file if the upload fails
    if (!file || !track?.id) return;
    // 12 MB matches the BE cap; anything larger is nearly certainly a raw
    // camera dump that we'd want the OS to compress first anyway.
    if (file.size > 12 * 1024 * 1024) {
      toast({ title: t('player.coverTooLarge', 'Image too large (max 12 MB)'), status: 'warning' });
      return;
    }
    try {
      // Shrink first — a 4032×3024 camera dump becomes ~2048px JPEG,
      // saving several megabytes on the upload (and matching what the
      // BE resizes to anyway).
      const dataUrl = await resizeImageForUpload(file, 2048);
      await uploadCoverMut({ jobId: track.id, imageDataUrl: dataUrl }).unwrap();
      toast({ title: t('player.coverUploaded', 'Cover updated'), status: 'success', duration: 2000 });
    } catch (e: any) {
      toast({ title: e?.data?.message || t('player.coverUploadFail', 'Upload failed — try again'), status: 'error' });
    }
  };

  // Swipe-down to close on touch devices. Keep it minimal — a real
  // gesture library is overkill for one interaction.
  const close = () => dispatch(collapse());

  // Auto-close if the queue empties while the full view is up.
  useEffect(() => {
    if (expanded && !track) close();
  }, [expanded, track]);

  if (!track) return null;
  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  const downloadWithTags = async () => {
    if (!track.audioUrl || !track.id) return;
    // Blob download — see src/download/downloadBlob.ts for full reasoning.
    // Mobile Chrome ignored `<a download>` on cross-origin URLs and opened
    // the audio in an in-app preview instead of saving.
    const token = (track as any).downloadToken;
    if (!token) {
      toast({ title: 'Refresh the page and try again', status: 'warning', duration: 2500 });
      return;
    }
    const proxyUrl = `${mixgeniePublicServiceURL}/songs/${track.id}/audio.mp3?tok=${encodeURIComponent(token)}`;
    const filename = `${(track.title || 'song').replace(/[^\w\-. ()]+/g, '_').slice(0, 80)}.mp3`;
    try {
      await downloadBlobFromUrl(proxyUrl, filename);
    } catch (e: any) {
      toast({ title: 'Download failed', description: String(e?.message || e), status: 'error', duration: 3000 });
    }
  };

  const share = async () => {
    if (!track.id) return;
    // Points at the public /s/:jobId share page — no auth needed for the
    // recipient. That's the whole point: virality requires a working
    // playable landing, otherwise every share hits an auth wall.
    const url = `${window.location.origin}/s/${track.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: track.title,
          text: `Listen to "${track.title}" — made with MixGenie`,
          url,
        });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast({ title: t('player.linkCopied', 'Share link copied'), status: 'success', duration: 1800 });
      } catch {
        toast({ title: url, status: 'info', duration: 4000 });
      }
    }
  };

  const openRename = () => {
    if (!track) return;
    setRenameDraft(track.title);
    renameModal.onOpen();
  };
  const submitRename = async () => {
    const next = renameDraft.trim();
    if (!next || !track?.id || next === track.title) { renameModal.onClose(); return; }
    setRenameBusy(true);
    try {
      await renameJob({ jobId: track.id, title: next }).unwrap();
      renameModal.onClose();
      // The polled job refresh + updateTrack mirror on HistoryPage will
      // propagate the new title into the player track. Nothing else to do.
    } catch (e: any) {
      toast({ title: e?.data?.message || 'Rename failed', status: 'error' });
    } finally {
      setRenameBusy(false);
    }
  };

  const generateSimilar = () => {
    if (!track?.id) return;
    // Same query-param dance as HistoryPage's "Generate similar" — the
    // generator page reads referenceTitle/vibe from URL to seed the form.
    const params = new URLSearchParams();
    params.set('vibe', track.title);
    close();
    navigate(`/generator?${params.toString()}`);
  };

  const deleteSong = async () => {
    if (!track?.id) return;
    const ok = window.confirm(t('player.confirmDelete', `Delete "${track.title}"? This removes the song permanently.`, { title: track.title }));
    if (!ok) return;
    try {
      await deleteJob({ jobId: track.id }).unwrap();
      toast({ title: t('player.deleted', 'Song deleted'), status: 'success', duration: 1800 });
      close();
    } catch (e: any) {
      toast({ title: e?.data?.message || 'Delete failed', status: 'error' });
    }
  };

  // Full-screen player is inset by the AppShell chrome (top header 56px +
  // bottom nav 68px), plus device safe-area insets. That keeps the tab
  // nav + credits badge accessible while the player is open — user asked
  // for this explicitly (2026-08-16). The overlay is intentionally light
  // so the chrome underneath still reads at a glance.
  return (
    <Modal isOpen={expanded} onClose={close} motionPreset="slideInBottom" size="full">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent
        bgGradient="linear(180deg, #1a0f2e 0%, #0f0b1f 100%)"
        color="whiteAlpha.900"
        m={0}
        borderRadius={{ base: '18px 18px 0 0', md: '20px' }}
        overflow="hidden"
        position="fixed"
        left={0}
        right={0}
        top="calc(56px + env(safe-area-inset-top, 0px))"
        bottom="calc(68px + env(safe-area-inset-bottom, 0px))"
        w="auto"
        h="auto"
        maxW="unset"
        maxH="unset"
        minH="unset"
        boxShadow="0 -24px 60px rgba(0,0,0,0.55)"
      >
        <Flex direction="column" h="full" px={5} pt={4} pb={{ base: 4, md: 6 }}>
          {/* Top bar */}
          <Flex justify="space-between" align="center" pb={2}>
            <IconButton
              aria-label="Close player"
              icon={<ChevronDown />}
              onClick={close}
              variant="ghost"
              color="whiteAlpha.800"
              size="lg"
            />
            <Stack spacing={0} textAlign="center" flex="1" px={2} minW={0}>
              <Text fontSize="10px" letterSpacing="0.16em" fontWeight={600} color="whiteAlpha.500" textTransform="uppercase">
                Now playing
              </Text>
              <Text fontSize="sm" color="whiteAlpha.700" noOfLines={1}>MixGenie</Text>
            </Stack>
            {/* Context menu — always accessible regardless of scroll or
                phone height, so the download/share/rename actions never
                fall off-screen the way the bottom actions row could. */}
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                aria-label="More actions"
                variant="ghost"
                color="whiteAlpha.800"
                size="lg"
                icon={
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                    <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
                  </svg>
                }
              />
              <Portal>
                <MenuList bg="night.800" borderColor="whiteAlpha.300" minW="240px" zIndex={2000}>
                  <MenuItem bg="night.800" color="whiteAlpha.900" _hover={{ bg: 'whiteAlpha.200' }} _focus={{ bg: 'whiteAlpha.200' }} onClick={share} icon={<Text as="span">↗</Text>}>
                    {t('player.menuShare', 'Share')}
                  </MenuItem>
                  <MenuItem bg="night.800" color="whiteAlpha.900" _hover={{ bg: 'whiteAlpha.200' }} _focus={{ bg: 'whiteAlpha.200' }} onClick={downloadWithTags} icon={<Text as="span">⬇</Text>}>
                    {t('player.menuDownload', 'Download')}
                  </MenuItem>
                  <MenuItem bg="night.800" color="whiteAlpha.900" _hover={{ bg: 'whiteAlpha.200' }} _focus={{ bg: 'whiteAlpha.200' }} onClick={openRename} icon={<EditIcon />}>
                    {t('player.menuRename', 'Rename')}
                  </MenuItem>
                  <MenuItem bg="night.800" color="whiteAlpha.900" _hover={{ bg: 'whiteAlpha.200' }} _focus={{ bg: 'whiteAlpha.200' }} onClick={generateSimilar} icon={<Text as="span">✨</Text>}>
                    {t('player.menuGenerateSimilar', 'Generate similar')}
                  </MenuItem>
                  <MenuItem
                    bg="night.800"
                    color="whiteAlpha.900"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    _focus={{ bg: 'whiteAlpha.200' }}
                    // Pause the global player first — the video studio starts its
                    // own audio graph and playing both would double the sound
                    // AND put audio through Web Audio while the recorder wants
                    // exclusive access to the element.
                    onClick={() => { dispatch(pause()); videoModal.onOpen(); }}
                    icon={<Text as="span">🎬</Text>}
                  >
                    {t('player.menuMakeVideo', 'Make video')}
                  </MenuItem>
                  <MenuDivider borderColor="whiteAlpha.200" />
                  <MenuItem bg="night.800" color="red.300" _hover={{ bg: 'red.900' }} _focus={{ bg: 'red.900' }} onClick={deleteSong} icon={<DeleteIcon />}>
                    {t('player.menuDelete', 'Delete')}
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          </Flex>

          {/* Cover — expands to fill available height/width */}
          <Flex flex="1" align="center" justify="center" py={{ base: 4, md: 8 }}>
            <Box
              maxW={{ base: '75vw', md: '360px' }}
              w="100%"
              aspectRatio={1}
              borderRadius="18px"
              overflow="hidden"
              boxShadow="0 20px 60px rgba(0,0,0,0.6)"
              position="relative"
            >
              {track.coverUrl ? (
                <Image src={track.coverUrl} alt="" w="100%" h="100%" objectFit="cover" />
              ) : (
                <>
                  {/* Ambient visualiser as the ground layer. Only ticks while
                      the modal is open + playing + tab visible. See RhythmBlobs. */}
                  <RhythmBlobs show={expanded} />
                  {/* pictools cross-sell CTA — overlaid at the bottom so it
                      doesn't fight the visualiser for attention. This is the
                      "no cover yet" prompt to spend 1 credit for artwork.
                      The row-menu on My Songs offers the same action; this
                      catches the moment when the user is actively looking
                      at the song and most likely to convert. */}
                  <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    p={3}
                    background="linear-gradient(to top, rgba(15, 11, 31, 0.85) 0%, rgba(15, 11, 31, 0) 100%)"
                    pointerEvents="none"
                  >
                    <Stack spacing={2} pointerEvents="auto">
                      <Button
                        onClick={generateCoverNow}
                        isDisabled={coverBusy || coverUploadBusy}
                        w="full"
                        h="auto"
                        py={2.5}
                        px={3}
                        bg="rgba(15, 11, 31, 0.75)"
                        color="whiteAlpha.900"
                        border="1px solid"
                        borderColor="whiteAlpha.300"
                        borderRadius="10px"
                        backdropFilter="blur(10px)"
                        _hover={{ bg: 'rgba(15, 11, 31, 0.9)', borderColor: 'accent.400' }}
                        _active={{ transform: 'scale(0.98)' }}
                        _disabled={{ opacity: 0.7, cursor: 'wait' }}
                        data-cta="fullplayer:generate-cover"
                      >
                        <HStack spacing={2} w="full" justify="center">
                          {coverBusy ? (
                            <Spinner size="xs" color="pink.300" />
                          ) : (
                            <PictoolsMark size={16} />
                          )}
                          <Stack spacing={0} align="flex-start" flex="0 1 auto" minW={0}>
                            <Text fontSize="sm" fontWeight={700} noOfLines={1}>
                              {coverBusy
                                ? t('player.coverPainting', 'Painting cover with pictools.ai…')
                                : t('player.coverCta', 'Generate cover with pictools.ai')}
                            </Text>
                            {!coverBusy && (
                              <Text fontSize="10px" color="whiteAlpha.600" fontWeight={600} letterSpacing="0.04em" textTransform="uppercase">
                                {t('player.coverCost', '1 credit')}
                              </Text>
                            )}
                          </Stack>
                        </HStack>
                      </Button>
                      {/* Secondary: upload from device gallery or capture with camera.
                          `accept="image/*"` + `capture="environment"` asks Android/iOS
                          to include camera in the picker sheet — the OS decides
                          which sources to show. */}
                      <Button
                        onClick={pickCoverFile}
                        isDisabled={coverBusy || coverUploadBusy}
                        w="full"
                        h="auto"
                        py={2}
                        px={3}
                        variant="ghost"
                        color="whiteAlpha.800"
                        _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                        _active={{ transform: 'scale(0.98)' }}
                        _disabled={{ opacity: 0.6, cursor: 'wait' }}
                        data-cta="fullplayer:upload-cover"
                      >
                        <HStack spacing={2} w="full" justify="center">
                          {coverUploadBusy ? <Spinner size="xs" /> : <Text fontSize="md">📷</Text>}
                          <Text fontSize="sm" fontWeight={600}>
                            {coverUploadBusy
                              ? t('player.coverUploading', 'Uploading cover…')
                              : t('player.coverUploadCta', 'Upload from device / camera')}
                          </Text>
                        </HStack>
                      </Button>
                    </Stack>
                    <Input
                      ref={coverFileInput}
                      type="file"
                      accept="image/*"
                      // Deliberately NO `capture` — on Chrome/Android that
                      // attribute forces the camera and skips the gallery.
                      // Without it, the OS picker offers gallery, camera
                      // and files together.
                      display="none"
                      onChange={onCoverFileChosen}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Flex>

          {/* Title + artist */}
          <Stack spacing={1} textAlign="center" px={2} mb={4}>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              fontWeight={700}
              color="whiteAlpha.900"
              noOfLines={1}
              px={2}
            >
              {track.title}
            </Text>
            <Text fontSize="sm" color="whiteAlpha.600" noOfLines={1}>
              {track.artist || 'MixGenie'}
            </Text>
          </Stack>

          {/* Seek bar */}
          <HStack spacing={3} px={2} mb={3} color="whiteAlpha.600" fontSize="xs">
            <Text w="42px" textAlign="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmt(progress)}
            </Text>
            <Slider
              aria-label="Seek"
              value={pct}
              min={0}
              max={100}
              onChange={(v) => {
                if (!duration) return;
                const t = (v / 100) * duration;
                // Playback element lives in GlobalAudioPlayer; dispatch a
                // seek that GAP's effect picks up and applies to audio.
                dispatch(seek(t));
                const audio = document.querySelector('audio') as HTMLAudioElement | null;
                if (audio) audio.currentTime = t;
              }}
              focusThumbOnChange={false}
              flex="1"
            >
              <SliderTrack bg="whiteAlpha.300" h="4px">
                <SliderFilledTrack bg="pink.400" />
              </SliderTrack>
              <SliderThumb boxSize={4} bg="pink.400" />
            </Slider>
            <Text w="42px" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmt(duration)}
            </Text>
          </HStack>

          {/* Transport row */}
          <Flex align="center" justify="space-between" px={2} mb={6}>
            <IconButton
              aria-label="Repeat"
              icon={<RepeatIcon mode={repeatMode} />}
              onClick={() => dispatch(cycleRepeat())}
              variant="ghost"
              color={repeatMode === 'off' ? 'whiteAlpha.500' : 'pink.400'}
              size="lg"
            />
            <IconButton
              aria-label="Previous"
              icon={<TransportGlyph d="M6 6h2v12H6zM9.5 12l8.5 6V6z" />}
              onClick={() => dispatch(prev())}
              variant="ghost"
              color="whiteAlpha.900"
              size="lg"
              fontSize="28px"
              isDisabled={index <= 0}
            />
            <IconButton
              aria-label={isPlaying ? 'Pause' : 'Play'}
              icon={isPlaying
                ? <TransportGlyph d="M6 5h4v14H6zM14 5h4v14h-4z" />
                : <TransportGlyph d="M8 5v14l11-7z" />
              }
              onClick={() => dispatch(isPlaying ? pause() : play())}
              bg="pink.500"
              color="white"
              _hover={{ bg: 'pink.400' }}
              _active={{ bg: 'pink.600' }}
              borderRadius="full"
              boxSize="70px"
              fontSize="34px"
            />
            <IconButton
              aria-label="Next"
              icon={<TransportGlyph d="M6 18l8.5-6L6 6v12zM16 6h2v12h-2z" />}
              onClick={() => dispatch(next())}
              variant="ghost"
              color="whiteAlpha.900"
              size="lg"
              fontSize="28px"
              isDisabled={index >= queue.length - 1}
            />
            <IconButton
              aria-label="Shuffle"
              icon={<ShuffleIcon />}
              onClick={() => dispatch(toggleShuffle())}
              variant="ghost"
              color={shuffle ? 'pink.400' : 'whiteAlpha.500'}
              size="lg"
            />
          </Flex>

          {/* Actions row — mirrors the reference: share · download · queue */}
          <Flex justify="space-around" pb={{ base: 4, md: 6 }} color="whiteAlpha.700">
            <IconButton
              aria-label="Share"
              icon={<GlyphShare />}
              onClick={share}
              variant="ghost"
              size="md"
              _hover={{ color: 'pink.300' }}
            />
            <IconButton
              aria-label="Download"
              icon={<GlyphDownload />}
              onClick={downloadWithTags}
              variant="ghost"
              size="md"
              _hover={{ color: 'pink.300' }}
            />
            <IconButton
              aria-label="Queue"
              icon={<GlyphQueue />}
              onClick={() => toast({ title: `${queue.length} in queue`, description: `Playing ${index + 1} of ${queue.length}`, status: 'info', duration: 2000 })}
              variant="ghost"
              size="md"
              _hover={{ color: 'pink.300' }}
            />
          </Flex>
        </Flex>
      </ModalContent>

      {/* Rename modal — same pattern as HistoryPage.JobRow. Kept here so
          "Edit" from the FullPlayer ⋮ works inline without navigating away
          from the currently-playing view. */}
      <Modal isOpen={renameModal.isOpen} onClose={renameModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900">
          <ModalHeader>{t('player.renameTitle', 'Rename song')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm" color="whiteAlpha.700">
                {t('player.renameLabel', 'Song title')}
              </FormLabel>
              <Input
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" colorScheme="whiteAlpha" onClick={renameModal.onClose} mr={2}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              colorScheme="pink"
              onClick={submitRename}
              isLoading={renameBusy}
              isDisabled={!renameDraft.trim() || renameDraft.trim() === track.title}
            >
              {t('save', 'Save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <VideoStudioModal
        isOpen={videoModal.isOpen}
        onClose={videoModal.onClose}
        track={track && track.audioUrl ? {
          id: track.id,
          title: track.title,
          artist: track.artist || 'MixGenie',
          audioUrl: track.audioUrl,
          coverUrl: track.coverUrl,
          // Player queue stashes the full job doc under `meta` (see
          // HistoryPage's playableTracks) — pluck lyrics from there for
          // the karaoke overlay. Prefer word-timed `syncedLyrics` when
          // present (Mureka via PromptLab.provider.raw as of 2026-08-22);
          // fall back to plain text for estimated timing.
          lyrics: (track.meta as any)?.lyrics,
          syncedLyrics: (track.meta as any)?.syncedLyrics,
        } : null}
      />
    </Modal>
  );
}

// ---------- Glyphs (inline SVG so no icon-font/CDN dependency) ---------

function TransportGlyph({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" style={{ display: 'block' }}>
      <path d={d} />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M7 10l5 5 5-5z" /></svg>
  );
}

function RepeatIcon({ mode }: { mode: 'off' | 'one' | 'all' }) {
  // Same glyph for all three; "one" gets a small "1" badge overlay.
  return (
    <Box position="relative">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ display: 'block' }}>
        <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z" />
      </svg>
      {mode === 'one' && (
        <Text
          position="absolute"
          top="-4px"
          right="-6px"
          fontSize="9px"
          fontWeight={800}
          bg="pink.400"
          color="white"
          borderRadius="full"
          w="14px"
          h="14px"
          lineHeight="14px"
          textAlign="center"
        >
          1
        </Text>
      )}
    </Box>
  );
}

function ShuffleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ display: 'block' }}>
      <path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z" />
    </svg>
  );
}

function GlyphShare() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ display: 'block' }}>
      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.15c-.05.21-.08.43-.08.66 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
    </svg>
  );
}

function GlyphDownload() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ display: 'block' }}>
      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
    </svg>
  );
}

function GlyphQueue() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ display: 'block' }}>
      <path d="M4 10h12v2H4zM4 6h12v2H4zM4 14h8v2H4zM14 14v6l5-3z" />
    </svg>
  );
}

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
